import React, { useState, useEffect } from 'react';
import { useDolphin } from '../../hooks/useDolphin';
import toast from 'react-hot-toast';
// PDF URLs for each level (now using /levels/levelX.pdf)
const levelPDFs = {
    1: '/levels/level1.pdf',
    2: '/levels/level2.pdf',
    3: '/levels/level3.pdf',
    4: '/levels/level4.pdf',
    5: '/levels/level5.pdf',
    6: '/levels/level6.pdf',
    7: '/levels/level7.pdf',
    8: '/levels/level8.pdf',
    9: '/levels/level9.pdf',
    10: '/levels/level10.pdf',
    11: '/levels/level11.pdf',
    12: '/levels/level12.pdf',
};

// Interactive descriptions for each level
const levelDescriptions = {
    1: "Kickstart your journey! Unlock the basics and get familiar with the platform.",
    2: "Level up! Dive deeper and discover new strategies to grow your skills.",
    3: "Advanced insights await. Access exclusive resources and expert tips.",
    4: "Pro status! Master the tools and maximize your potential.",
    5: "Elite access! Join the top tier and unlock all premium content.",
};


const LevelsTab = () => {
    const { purchaseLevel, purchaseLevels, userStats, fetchUserStats, actionError, levels, loading } = useDolphin();
    const [unlockedLevels, setUnlockedLevels] = useState([]);
    const [purchasingId, setPurchasingId] = useState(null);
    const [selectedLevels, setSelectedLevels] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    // Modal for viewing content
    const [showResourceModal, setShowResourceModal] = useState(false);
    const [modalLevel, setModalLevel] = useState(null);
    // Removed unused variables: showReferralModal, referralModalInfo, showReferralPrompt, pendingReferralCode

    // Initialize unlocked levels - start empty, will update from purchases
    useEffect(() => {
        // Start with empty array, we'll add levels as they're purchased
        setUnlockedLevels([]);
        
        // Try to get from userStats if available (fallback)
        if (userStats && userStats.levels && userStats.levels.length > 0) {
            setUnlockedLevels(userStats.levels);
        }
    }, [userStats]);

    // Update unlocked levels when userStats updates
    useEffect(() => {
        if (userStats && userStats.levels && userStats.levels.length > 0) {
            setUnlockedLevels(prev => {
                // Merge with existing levels
                const merged = [...prev, ...userStats.levels];
                return [...new Set(merged)].sort((a, b) => a - b);
            });
        }
    }, [userStats]);

    const maxUnlocked = unlockedLevels.length > 0 ? Math.max(...unlockedLevels) : 0;
    const nextPurchasableLevel = maxUnlocked + 1;

    // Calculate total price for selected levels
    const calculateTotalPrice = () => {
        return selectedLevels.reduce((total, levelId) => {
            const level = levels.find(l => l.id === levelId);
            return total + (level ? parseFloat(level.price) : 0);
        }, 0);
    };

    // Main purchase handler - FIXED: No referrer parameter
    const handlePurchase = async () => {
        if (selectedLevels.length === 0 || isProcessing) return;
        setIsProcessing(true);
        try {
            const totalUsd = calculateTotalPrice();
            if (selectedLevels.length === 1) {
                // Single level purchase
                await purchaseLevel(selectedLevels[0]);
                toast.success(`✓ Purchased Level ${selectedLevels[0]}!`);
            } else {
                // Batch purchase
                await purchaseLevels(selectedLevels, totalUsd);
                toast.success(`✓ Purchased ${selectedLevels.length} level(s)!`);
            }
        } catch (err) {
            let userMessage = 'Transaction failed.';
            if (err?.message?.includes('rejected') || err?.code === 4001) {
                userMessage = 'Transaction cancelled by user.';
            } else if (err?.message?.toLowerCase().includes('insufficient funds') || err?.message?.toLowerCase().includes('insufficient usdt')) {
                userMessage = 'Insufficient balance. Please check your USDT balance.';
            } else if (err?.message?.includes('already own')) {
                userMessage = 'You already own one or more selected levels.';
            } else if (err?.message?.toLowerCase().includes('previous level')) {
                userMessage = 'You must purchase previous levels first.';
            } else if (err?.message?.includes('execution reverted')) {
                userMessage = 'Transaction reverted. Please check requirements.';
            }
            toast.error(userMessage, {
                duration: 6000,
                position: 'top-center',
                style: { background: '#fff', color: '#d32f2f', fontWeight: 'bold', fontSize: '1rem' },
            });
        } finally {
            setIsProcessing(false);
            setPurchasingId(null);
            fetchUserStats();
        }
    };


    // Checkbox selection logic
    const handleSelectLevel = (levelId) => {
        if (levelId < nextPurchasableLevel) return; // Can't select already purchased
        
        if (selectedLevels.includes(levelId)) {
            // Deselect this and all higher levels
            const index = selectedLevels.indexOf(levelId);
            setSelectedLevels(selectedLevels.slice(0, index));
        } else {
            // Only allow selecting sequential levels starting from nextPurchasableLevel
            const expectedNextId = nextPurchasableLevel + selectedLevels.length;
            if (levelId === expectedNextId) {
                setSelectedLevels([...selectedLevels, levelId]);
            } else {
                toast.error(`Please purchase level ${expectedNextId} first`);
            }
        }
    };

    // Clear all selections
    const handleClearSelections = () => {
        setSelectedLevels([]);
    };

    // Handler to open modal for a level
    const handleViewContent = (levelId) => {
        setModalLevel(levelId);
        setShowResourceModal(true);
    };

    // Removed unused function: handleReferralModalSubmit

        // Removed unused function: handleReferralPromptSubmit

    if (showResourceModal && modalLevel) {
        return (
            <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'#181f2a',zIndex:1000}}>
                <button onClick={()=>setShowResourceModal(false)} style={{position:'absolute',top:18,right:24,background:'none',border:'none',color:'#a78bfa',fontSize:32,fontWeight:700,cursor:'pointer',zIndex:1001}}>×</button>
                <iframe
                    src={levelPDFs[modalLevel]}
                    title={`Level ${modalLevel} PDF`}
                    style={{width:'100vw',height:'100vh',border:'none',background:'#181f2a'}}
                />
            </div>
        );
    }

    if (loading || !levels || levels.length === 0) {
        return (
            <div className="wallet-container fade-in bg-gradient-to-br from-gray-950 to-gray-900 min-h-screen p-4 lg:p-6 rounded-2xl">
                <div className="levels-content-section" style={{width:'100%',margin:'0',padding:'0'}}>
                    <div className="analytics-card" style={{background:'#18181b',borderRadius:8,padding:'1.2rem',marginBottom:'1.5rem',boxShadow:'0 8px 32px #181f2a33', textAlign:'left'}}>
                        <h1 className="levels-title-responsive" style={{ color: '#fff', fontSize: '1.4rem', fontWeight: 900, letterSpacing: 1.2, marginBottom: 8, textShadow: '0 4px 32px #a78bfa55, 0 1.5px 0 #38bdf8' }}>
                            Educational Packages
                        </h1>
                        <h2 style={{ color: '#a78bfa', fontSize: '0.7rem', fontWeight: 400, marginBottom: 22, letterSpacing: 0.7, textShadow: '0 1.5px 8px #23263a', maxWidth: 600 }}>
                            Unlock premium levels and resources. Select multiple levels in order to upgrade faster.
                        </h2>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <div style={{ color: '#a78bfa', fontSize: '1.2rem' }}>Loading levels...</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="wallet-container fade-in bg-gradient-to-br from-gray-950 to-gray-900 min-h-screen p-4 lg:p-6 rounded-2xl">
            <div className="levels-content-section" style={{width:'100%',margin:'0',padding:'0'}}>
                <div className="analytics-card bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-2 sm:p-4 mb-4 shadow text-left">
                    <h1 className="levels-title-responsive text-white text-lg sm:text-xl font-bold tracking-tight mb-1">Educational Packages</h1>
                    <h2 className="text-purple-300 text-xs sm:text-sm font-normal mb-3 tracking-tight max-w-xl">Unlock premium levels and resources. Select multiple levels in order to upgrade faster.</h2>
                </div>
                <div className="flex flex-col sm:flex-row justify-between items-center mb-3 bg-gray-800/80 rounded-lg p-2 sm:p-4 shadow border border-purple-300/30 backdrop-blur-sm flex-wrap min-h-0">
                    <div className="flex flex-col gap-1 bg-gradient-to-br from-gray-950 to-gray-900 rounded-md px-3 py-2 mb-2 min-w-[180px] border border-purple-400/30 shadow-sm">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 font-medium">Current Level:</span>
                            <span className="text-purple-300 font-bold text-xs sm:text-sm">{unlockedLevels.length > 0 ? (levels.find(l => l.id === maxUnlocked)?.name || `Level ${maxUnlocked}`) : 'None'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 font-medium">Next Available:</span>
                            <span className="text-white font-bold text-xs sm:text-sm">Level {nextPurchasableLevel}</span>
                        </div>
                        {selectedLevels.length > 0 && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400 font-medium">Selected:</span>
                                <span className="text-white font-bold text-xs sm:text-sm">{selectedLevels.length} level(s)</span>
                                <span className="text-purple-300 font-bold text-xs sm:text-sm">- {calculateTotalPrice()} USDT</span>
                            </div>
                        )}
                    </div>
                    
                    {actionError && (
                        <div style={{ color: 'red', marginBottom: 10, fontWeight: 600, fontSize: '1.08rem' }}>
                            {actionError}
                        </div>
                    )}

                    <div className="flex items-center gap-2">
                        {selectedLevels.length > 0 && (
                            <>
                                <div className="text-purple-300 font-bold text-sm mb-2">
                                    Total to be deducted: {calculateTotalPrice()} USDT
                                </div>
                                <button
                                    onClick={handleClearSelections}
                                    className="bg-transparent text-red-400 border border-red-400 rounded px-2 py-1 font-semibold text-xs sm:text-sm cursor-pointer disabled:opacity-60"
                                    disabled={isProcessing}
                                >
                                    Clear
                                </button>
                            </>
                        )}
                        <button
                            className={`level-purchase-btn responsive font-bold text-xs sm:text-sm rounded px-3 py-1 sm:px-5 sm:py-2 transition-all duration-150 ${selectedLevels.length === 0 ? 'bg-gray-600 text-gray-200 cursor-not-allowed' : 'bg-gradient-to-r from-purple-400 to-cyan-400 text-gray-900 shadow-lg cursor-pointer'} ${isProcessing ? 'opacity-60' : ''}`}
                            onClick={handlePurchase}
                            disabled={selectedLevels.length === 0 || isProcessing}
                        >
                            {isProcessing ? (
                                <span className="flex items-center gap-1">
                                    <span className="spinner inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    {purchasingId ? `Buying Level ${purchasingId}` : 'Processing...'}
                                </span>
                            ) : selectedLevels.length === 0 ? (
                                'Select Levels'
                            ) : (
                                `Purchase ${selectedLevels.length} Level(s)`
                            )}
                        </button>
                    </div>
                    <style>{`
                        @media (max-width: 900px) {
                            .current-level-responsive {
                                font-size: 1rem !important;
                            }
                            .level-purchase-btn.responsive {
                                font-size: 1rem !important;
                                padding: 0.5rem 1.2rem !important;
                                border-radius: 8px !important;
                            }
                        }
                        @media (max-width: 600px) {
                            .current-level-responsive {
                                font-size: 0.85rem !important;
                            }
                            .current-level-label {
                                font-size: 0.9rem !important;
                            }
                            .level-purchase-btn.responsive {
                                font-size: 0.8rem !important;
                                padding: 0.3rem 0.7rem !important;
                                border-radius: 6px !important;
                            }
                        }
                    `}</style>
                </div>
                <div className="levels-list flex flex-wrap gap-4 sm:gap-8 justify-center">
                    {levels.map((level) => {
                        const isUnlocked = unlockedLevels.includes(level.id);
                        const isNextPurchasable = level.id === nextPurchasableLevel;
                        const isSelected = selectedLevels.includes(level.id);
                        const canBuy = !isUnlocked && level.id >= nextPurchasableLevel;

                        return (
                            <div key={level.id} className={`level-card dashboard-card ${isUnlocked ? 'bg-[#181f2a] border-2 border-purple-400/70' : 'bg-[#181f2a] border-2 border-[#23263a]'} text-white rounded-xl shadow-md min-w-[220px] max-w-xs p-3 sm:p-5 mb-3 relative transition-all duration-150` }>
                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center mb-1">
                                        <span className="level-icon text-lg sm:text-2xl mr-2">{level.icon}</span>
                                        <span className="level-topic-title font-bold text-xs sm:text-base flex-1">
                                            {`LEVEL ${level.id}`}
                                        </span>
                                        {!isUnlocked && (
                                            <div className="flex flex-col items-center gap-1 ml-2">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => handleSelectLevel(level.id)}
                                                    disabled={!canBuy || isProcessing}
                                                    className="accent-purple-400 scale-90 cursor-pointer disabled:cursor-not-allowed"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="level-price text-purple-300 font-semibold text-xs sm:text-sm mb-1">
                                        {level.price} <span className="text-[10px] sm:text-xs font-normal align-middle">USDT</span>
                                    </div>
                                    <div className="level-topic text-gray-200 text-xs sm:text-sm font-medium mb-1">
                                        {Array.isArray(level.pdfTopics) && level.pdfTopics.length > 0
                                            ? level.pdfTopics[0]
                                            : (level.topic ? level.topic : '')}
                                    </div>
                                    <div className="level-description text-purple-300 text-xs sm:text-sm font-normal mb-2 min-h-[20px]">
                                        {levelDescriptions[level.id] || "Unlock this level to discover more!"}
                                    </div>
                                </div>
                                {/* Quick Buy button removed */}
                                {!isUnlocked && !canBuy && !isNextPurchasable && (
                                    <button className="level-purchase-btn w-full bg-[#181f2a] text-purple-300 border border-[#23263a] rounded py-1 mt-2 font-semibold text-xs sm:text-sm opacity-60 cursor-not-allowed" disabled>Unlock previous level first</button>
                                )}
                                {isUnlocked ? (
                                    <button 
                                        className="level-purchase-btn w-full bg-gradient-to-r from-cyan-400 to-purple-400 text-gray-900 rounded py-1 mt-2 font-bold text-xs sm:text-sm shadow cursor-pointer"
                                        onClick={() => handleViewContent(level.id)}
                                    >
                                        View
                                    </button>
                                ) : (
                                    <button 
                                        className="level-purchase-btn w-full bg-[#181f2a] text-purple-300 border border-[#23263a] rounded py-1 mt-2 font-semibold text-xs sm:text-sm opacity-60 cursor-not-allowed"
                                        disabled
                                    >
                                        Locked
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
                {/* Content and multilevel status are now only visible in the modal when 'View' is clicked */}
                
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
                {/* Modal for viewing content */}
                {showResourceModal && modalLevel && (
                    <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'#181f2a',zIndex:1000}}>
                        <button onClick={()=>setShowResourceModal(false)} style={{position:'absolute',top:18,right:24,background:'none',border:'none',color:'#a78bfa',fontSize:32,fontWeight:700,cursor:'pointer',zIndex:1001}}>×</button>
                        <iframe
                            src={levelPDFs[modalLevel]}
                            title={`Level ${modalLevel} PDF`}
                            style={{width:'100vw',height:'100vh',border:'none',background:'#181f2a'}}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

// Removed unused component: ReferralCodePrompt

export default LevelsTab;